import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Volume2, VolumeX, Play, Pause } from "lucide-react";

export const PortfolioModal = ({ project, onClose }) => {
  const videoRef = useRef(null);
  const [muted, setMuted] = useState(false);
  const [playing, setPlaying] = useState(true);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [onClose]);

  useEffect(() => {
    if (!videoRef.current) return;
    // Try to play with sound on open (user gesture came from click)
    videoRef.current.muted = false;
    setMuted(false);
    videoRef.current.play().catch(() => {
      // Fallback to muted autoplay if browser blocks
      if (videoRef.current) {
        videoRef.current.muted = true;
        setMuted(true);
        videoRef.current.play().catch(() => {});
      }
    });
  }, []);

  const toggleMute = () => {
    if (!videoRef.current) return;
    videoRef.current.muted = !videoRef.current.muted;
    setMuted(videoRef.current.muted);
  };

  const togglePlay = () => {
    if (!videoRef.current) return;
    if (videoRef.current.paused) {
      videoRef.current.play();
      setPlaying(true);
    } else {
      videoRef.current.pause();
      setPlaying(false);
    }
  };

  return (
    <AnimatePresence>
      {project && (
        <motion.div
          data-testid="portfolio-modal"
          className="fixed inset-0 z-[80] flex items-center justify-center px-4 md:px-8 py-8"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
        >
          {/* Backdrop */}
          <motion.button
            type="button"
            aria-label="Cerrar"
            data-testid="modal-backdrop"
            onClick={onClose}
            className="absolute inset-0 bg-black/90 backdrop-blur-md cursor-default"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          />

          {/* Content shell with layoutId that morphs from the card */}
          <motion.div
            layoutId={`card-${project.id}`}
            transition={{ type: "spring", stiffness: 130, damping: 20 }}
            className="relative z-10 w-full max-w-[420px] md:max-w-[460px] aspect-[9/16] bg-black border border-divider overflow-hidden"
          >
            <video
              ref={videoRef}
              src={project.src}
              autoPlay
              loop
              playsInline
              controls={false}
              className="absolute inset-0 w-full h-full object-cover"
              data-testid="modal-video"
            />

            {/* Top row inside modal */}
            <div className="absolute top-0 inset-x-0 flex items-center justify-between px-4 py-3 z-20 bg-gradient-to-b from-black/70 to-transparent">
              <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/80">
                [{project.index}] · {project.year}
              </div>
              <button
                type="button"
                onClick={onClose}
                data-testid="modal-close"
                aria-label="Cerrar modal"
                className="w-9 h-9 flex items-center justify-center border border-white/20 hover:border-rust hover:text-rust text-white/90 transition-colors"
              >
                <X size={16} />
              </button>
            </div>

            {/* Bottom row inside modal */}
            <div className="absolute bottom-0 inset-x-0 z-20 bg-gradient-to-t from-black via-black/70 to-transparent px-4 py-4">
              <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-rust mb-1">
                {project.category}
              </div>
              <div className="font-display uppercase text-xl md:text-2xl leading-tight text-white mb-2">
                {project.title}
              </div>
              <div className="font-mono text-[11px] leading-relaxed text-white/70 mb-4 max-w-[38ch]">
                {project.description}
              </div>

              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={togglePlay}
                  data-testid="modal-play-toggle"
                  aria-label={playing ? "Pausar" : "Reproducir"}
                  className="w-9 h-9 flex items-center justify-center border border-white/20 hover:border-rust hover:text-rust text-white/90 transition-colors"
                >
                  {playing ? <Pause size={14} /> : <Play size={14} />}
                </button>
                <button
                  type="button"
                  onClick={toggleMute}
                  data-testid="modal-mute-toggle"
                  aria-label={muted ? "Activar sonido" : "Silenciar"}
                  className="w-9 h-9 flex items-center justify-center border border-white/20 hover:border-rust hover:text-rust text-white/90 transition-colors"
                >
                  {muted ? <VolumeX size={14} /> : <Volume2 size={14} />}
                </button>
                <div className="flex-1" />
                <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/50">
                  ASOMBRO / REEL
                </div>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
